import React, { useEffect, useMemo, useState } from "react";
import {
  Notice,
  fetchVisibleNotices,
  dismissNotice,
  isDismissed,
} from "../services/NoticeService";
import NoticePopup from "./NoticePopup";

const TONE = {
  info: { fg: "#5EE9B5", bg: "rgba(94,233,181,0.10)", br: "rgba(94,233,181,0.30)" },
  warning: { fg: "#F5B44C", bg: "rgba(245,180,76,0.10)", br: "rgba(245,180,76,0.30)" },
  critical: { fg: "#F0616B", bg: "rgba(240,97,107,0.12)", br: "rgba(240,97,107,0.32)" },
};

/**
 * Site-wide notices shown on every page.
 *
 * Renders nothing unless at least one notice is active AND inside its expiry
 * window, so a forgotten banner is covered by the auto-hide.
 */
const NoticeBanner: React.FC = () => {
  /** Everything currently publishable, before per-device dismissal. */
  const [all, setAll] = useState<Notice[]>([]);
  /** Bumped to re-evaluate dismissal windows without refetching. */
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      fetchVisibleNotices().then((list) => {
        if (!cancelled) setAll(list);
      });
    };

    load();

    // This is a SPA people leave open, so "reopening the site" is usually
    // returning to an existing tab rather than a fresh mount. Refetch on focus
    // to pick up newly published notices...
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        load();
        setTick((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    // ...and re-evaluate dismissal locally on a timer, so a popup reappears
    // once its cool-off elapses. This costs no Firestore reads.
    const timer = setInterval(() => setTick((t) => t + 1), 60_000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const notices = useMemo(() => all.filter((n) => !isDismissed(n)), [all, tick]);

  if (!notices.length) return null;

  const banners = notices.filter((n) => n.display !== "popup");
  // Only one popup at a time — stacking full-screen modals is unusable. The
  // newest wins, since fetchVisibleNotices returns newest-first.
  const popup = notices.find((n) => n.display === "popup");

  const drop = (n: Notice) => {
    dismissNotice(n);
    // Re-derive from `all` rather than removing it: the notice is still
    // publishable, it is just dismissed until its cool-off expires.
    setTick((t) => t + 1);
  };

  return (
    <>
      {popup && <NoticePopup notice={popup} onDismiss={() => drop(popup)} />}
      {banners.map((notice) => {
        const tone = TONE[notice.tone] ?? TONE.warning;
        return (
          <div
            key={notice.id}
            style={{
              background: tone.bg,
              borderBottom: `1px solid ${tone.br}`,
              padding: "12px 16px",
              fontFamily: "'Sora',sans-serif",
            }}
          >
            <div
              style={{
                maxWidth: 1120,
                margin: "0 auto",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={tone.fg}
                strokeWidth="2"
                strokeLinecap="round"
                style={{ flex: "none", marginTop: 1 }}
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16h.01" />
              </svg>

              {/* Text and image render together — an image supplements the
                  message rather than replacing it. */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {notice.title && (
                  <div style={{ fontSize: 14, fontWeight: 600, color: tone.fg }}>
                    {notice.title}
                  </div>
                )}
                {notice.body && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#D6E2DC",
                      marginTop: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    {notice.body}
                  </div>
                )}
                {notice.imageUrl && (
                  <img
                    src={notice.imageUrl}
                    alt={notice.title || "Notice"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 180,
                      height: "auto",
                      borderRadius: 8,
                      display: "block",
                      marginTop: notice.title || notice.body ? 8 : 0,
                    }}
                  />
                )}
              </div>

              <button
                onClick={() => drop(notice)}
                aria-label="Dismiss notice"
                style={{
                  flex: "none",
                  background: "transparent",
                  border: 0,
                  color: tone.fg,
                  opacity: 0.7,
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: 2,
                }}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default NoticeBanner;
